import axios from 'axios'
import type { AuthProvider, AuthSession, MfaProvider } from './AuthProvider'
import { NoInAppMfa, unsupported } from './NoInAppMfa'
import { config } from '@/config'
import { Configuration as SecurityConfig, SecurityApi, type ImpersonationSessionResponse } from '@/api/generated-security'
import type { User } from '@/types/user'

/**
 * The prefix of an impersonation token the backend issued itself rather than through Firebase —
 * what an admin signed in through Keycloak receives (ADR-045). Written twice, here and in
 * `ImpersonationSessionToken.PREFIX` on the backend, and the two must agree.
 */
export const IMPERSONATION_SESSION_TOKEN_PREFIX = 'scrivio-imp_'

export function isImpersonationSessionToken(token: string): boolean {
  return token.startsWith(IMPERSONATION_SESSION_TOKEN_PREFIX)
}

/** The query or fragment parameter the app's `/impersonate` page reads the token from. */
export const IMPERSONATION_TOKEN_PARAM = 'impersonation_token'

/**
 * The link an admin opens to start an impersonated session. A backend-issued token rides in the
 * fragment, which the browser never sends, so it reaches no request log on the way; a Firebase
 * custom token keeps the query parameter it has always had.
 */
export function impersonationLink(redirectUrl: string, token: string): string {
  const separator = isImpersonationSessionToken(token) ? '#' : '?'
  return `${redirectUrl}${separator}${IMPERSONATION_TOKEN_PARAM}=${encodeURIComponent(token)}`
}

/** The token an `/impersonate` link carries, from its fragment or its query. */
export function impersonationTokenFrom(hash: string, query: unknown): string | undefined {
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get(IMPERSONATION_TOKEN_PARAM)
  if (fromHash) return fromHash
  return typeof query === 'string' && query ? query : undefined
}

const STORAGE_KEY = 'scrivio.impersonationSession'

interface StoredSession {
  token: string
  description: ImpersonationSessionResponse
}

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredSession) : null
  } catch {
    return null
  }
}

/**
 * An impersonated session whose bearer token the backend issued: opaque to the app, so who is
 * acting as whom is asked of the API once, at sign-in, and kept beside the token. Both live in
 * `sessionStorage`, so the session belongs to the one tab it was opened in and ends with it.
 *
 * Nothing here refreshes: the token is valid until the backend says it is not, and that refusal
 * is the `impersonation-expired` 401 `useImpersonation` already ends a session on.
 */
export class ImpersonationSessionAuthService implements AuthProvider {
  readonly mfa: MfaProvider = new NoInAppMfa()
  private stored: StoredSession | null = readStored()
  private readonly listeners = new Set<(user: User | null) => void>()

  private user(): User | null {
    const description = this.stored?.description
    if (!description) return null
    return {
      uid: description.targetUserId,
      email: description.targetEmail,
      displayName: null,
      emailVerified: description.targetEmailVerified,
    }
  }

  /** Signs in with the token from the admin portal's impersonation link. */
  async signInWithCustomToken(token: string): Promise<void> {
    const client = axios.create({
      baseURL: config.apiBaseUrl,
      headers: { Authorization: `Bearer ${token}` },
    })
    const { data } = await new SecurityApi(new SecurityConfig(), undefined, client).getImpersonationSession()
    this.stored = { token, description: data }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.stored))
    this.notify()
  }

  async signOut(): Promise<void> {
    this.stored = null
    sessionStorage.removeItem(STORAGE_KEY)
    this.notify()
  }

  async getCurrentUser(): Promise<User | null> {
    return this.user()
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    this.listeners.add(callback)
    void Promise.resolve().then(() => callback(this.user()))
    return () => {
      this.listeners.delete(callback)
    }
  }

  async handleRedirectResult(): Promise<User | null> {
    return null
  }

  currentSession(): AuthSession | null {
    const user = this.user()
    if (!user) return null
    return { uid: user.uid, email: user.email, emailVerified: user.emailVerified ?? false }
  }

  async getIdToken(): Promise<string | null> {
    return this.stored?.token ?? null
  }

  /** The impersonation claims a Firebase session carries in its token, in the same names. */
  async getTokenClaims(): Promise<Record<string, unknown> | null> {
    const description = this.stored?.description
    if (!description) return null
    return {
      impersonatedBy: description.adminUserId,
      impersonatedByEmail: description.adminEmail ?? undefined,
      impersonationStartedAt: description.startedAt ?? undefined,
    }
  }

  isSessionExpiredError(): boolean {
    return false
  }

  async signIn(): Promise<User> {
    throw unsupported('Signing in with a password during impersonation')
  }

  async signUp(): Promise<User> {
    throw unsupported('Sign-up during impersonation')
  }

  async signInWithSSO(): Promise<User> {
    throw unsupported('SSO during impersonation')
  }

  async sendEmailVerification(): Promise<void> {
    throw unsupported('Sending a verification e-mail during impersonation')
  }

  isSignInLink(): boolean {
    return false
  }

  async signInWithEmailLink(): Promise<void> {
    throw unsupported('Signing in with an e-mail link during impersonation')
  }

  private notify(): void {
    const user = this.user()
    this.listeners.forEach((listener) => listener(user))
  }
}
